import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { DownloadIcon, Loader2, X } from "lucide-react";
import { cn, useOverlayLock, OVERLAY_LOCKED_CLASSES } from "@nexus/ui";

interface ImagePreviewTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  src: string | null | undefined;
  alt: string;
  children: React.ReactNode;
}

// ─── Lightbox ────────────────────────────────────────────────────────────────
// Rendered into <body> via portal. Must NOT let pointer events bubble to the
// document, or Radix Dialog/Popover outside-click handlers will fire and tear
// down the underlying modal — which leaves `pointer-events:none` stuck on
// <body> (the page becomes unhoverable until reload).

function LightboxPortal({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isLocked = useOverlayLock();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const prevSrcRef = useRef(src);

  // Reset during render so the first paint of a new `src` is not stuck on the
  // previous image's loaded/error UI (an effect would flash that stale state).
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    setLoaded(false);
    setError(false);
    setDownloading(false);
  }

  // A warm image can finish decoding before React attaches onLoad, so that event
  // never fires and the spinner would sit there forever. Settle from the element.
  useEffect(() => {
    const img = imgRef.current;
    if (!img?.complete) return;
    if (img.naturalWidth === 0) setError(true);
    else setLoaded(true);
  }, [src]);

  // Keep a stable ref to onClose so the effect below only runs once per mount.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isLockedRef = useRef(isLocked);
  isLockedRef.current = isLocked;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLockedRef.current) onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = src.split("/").pop()?.split("?")[0] ?? "image";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(src, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  // Block every pointer/mouse event from reaching the document so Radix
  // outside-click detectors (Dialog, Popover, DropdownMenu, …) ignore us.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return createPortal(
    <div
      // z-(--z-modal): on the shared layer scale so the connection-status pill
      // (--z-connection-status) stays clickable above the lightbox while offline.
      className={cn(
        "fixed inset-0 z-(--z-modal) flex items-center justify-center bg-black/85",
        isLocked && OVERLAY_LOCKED_CLASSES,
      )}
      // pointer-events:auto keeps Radix outside-click detectors from seeing
      // through the lightbox while online; drop it when locked so the class
      // `pointer-events-none` from OVERLAY_LOCKED_CLASSES can take effect.
      style={isLocked ? undefined : { pointerEvents: "auto" }}
      inert={isLocked || undefined}
      aria-busy={isLocked || undefined}
      onClick={isLocked ? undefined : onClose}
      onPointerDown={stop}
      onPointerDownCapture={stop}
      onMouseDown={stop}
      onMouseDownCapture={stop}
      onPointerUp={stop}
      onMouseUp={stop}
    >
      <div
        className="relative flex flex-col overflow-hidden rounded-md bg-neutral-950 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] max-h-[92dvh] w-auto max-w-[92dvw]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-black/60 text-white/70 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
        >
          <X className="size-4" />
          <span className="sr-only">{t("common.close", "Close")}</span>
        </button>

        {error ? (
          <div className="flex h-52 w-72 items-center justify-center">
            <p className="text-sm text-white/40">
              {t("iam.imagePreview.loadFailed", "Failed to load image")}
            </p>
          </div>
        ) : (
          // The image stays mounted and only fades in, so a cached hit paints
          // immediately instead of being held behind `hidden` until onLoad.
          <div className="relative flex min-h-52 min-w-72 items-center justify-center">
            <img
              key={src}
              ref={imgRef}
              src={src}
              alt={alt}
              draggable={false}
              className={cn(
                "max-h-[80dvh] max-w-[88dvw] transition-opacity duration-150 ease-out motion-reduce:transition-none",
                loaded ? "opacity-100" : "opacity-0",
              )}
              onLoad={() => setLoaded(true)}
              onError={() => setError(true)}
            />
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="size-8 animate-spin text-white/40" />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <p className="truncate text-sm font-medium text-white/80">{alt}</p>
          {!error && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/20 hover:text-white disabled:opacity-50"
            >
              {downloading
                ? <Loader2 className="size-3.5 animate-spin" />
                : <DownloadIcon className="size-3.5" />}
              {downloading
                ? t("iam.imagePreview.downloading", "Downloading…")
                : t("iam.imagePreview.download", "Download")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Trigger ─────────────────────────────────────────────────────────────────
// `forwardRef` + `...rest` so this works as a Radix `<TooltipTrigger asChild>`
// (or any other `asChild` consumer). Without it, Radix can't attach hover
// handlers, refs, or `data-state` and the tooltip silently breaks.

export const ImagePreviewTrigger = forwardRef<HTMLButtonElement, ImagePreviewTriggerProps>(
  function ImagePreviewTrigger({ src, alt, children, onClick, className, ...rest }, ref) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick?.(e);
        if (!e.defaultPrevented) setOpen(true);
      },
      [onClick],
    );

    if (!src) return <>{children}</>;

    return (
      <>
        <button
          {...rest}
          ref={ref}
          type="button"
          className={
            "inline-flex shrink-0 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1" +
            (className ? ` ${className}` : "")
          }
          aria-label={t("iam.imagePreview.previewAria", "Preview {{alt}}", { alt })}
          aria-haspopup="dialog"
          onClick={handleClick}
        >
          {children}
        </button>

        {open && <LightboxPortal src={src} alt={alt} onClose={() => setOpen(false)} />}
      </>
    );
  },
);
